import { useEffect, useState, useCallback, useMemo } from "react";
import {
  ChevronRight,
  AlertTriangle,
  Loader2,
  ShieldCheck,
  History,
  Lock,
  BadgeCheck,
  MapPin,
  Tag,
  PlusCircle,
  RefreshCw,
  Maximize
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import usePaymentStore from "../../../store/builders/usePaymentStore";
import useAuthStore from "../../../store/useAuthStore";
import useProfileStore from "../../../store/builders/useProfileStore";
import useServiceAreaStore from "../../../store/builders/useServiceAreaStore";
import useBuilderStatusStore from "../../../store/builders/useBuilderStatusStore";

import ServiceAreaExpansionModal from "./ServiceAreaExpansionModal";
import SubscriptionHistoryTile from "./SubscriptionHistoryTile";
import toast from "react-hot-toast";
import { load } from "@cashfreepayments/cashfree-js";

const SubscriptionTile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState("payment"); // 'payment' or 'history'

  const { user } = useAuthStore.getState();
  const { profile, fetchProfile } = useProfileStore();

  const {
    builderServiceAreas,
    totalServiceAreaCost,
    fetchBuilderServiceAreas,
    fetchPendingExpansionRequests,
    customOffer: serverCustomOffer,
    hasCustomOffer: serverHasCustomOffer,
    isLoading: isAreasLoading
  } = useServiceAreaStore();

  const {
    createCashfreeOrder,
    verifyCashfreePayment,
    isCreatingOrder,
    isVerifyingPayment
  } = usePaymentStore();

  const [cashfree, setCashfree] = useState(null);
  const [isExpansionModalOpen, setIsExpansionModalOpen] = useState(false);
  const [restoredOffer, setRestoredOffer] = useState(null);

  const customOffer = serverCustomOffer || restoredOffer;
  const hasCustomOffer = serverHasCustomOffer || !!restoredOffer;

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("activeSubscriptionOffer");
      if (saved) setRestoredOffer(JSON.parse(saved));
    } catch (e) {
      console.error("Failed to restore offer", e);
    }
  }, []);

  useEffect(() => {
    const loadCF = async () => {
      try {
        const mode = process.env.REACT_APP_CASHFREE_MODE || "production";
        const cf = await load({ mode });
        setCashfree(cf);
      } catch (err) {
        toast.error("Unable to load payment SDK");
      }
    };
    loadCF();
  }, []);

  const stableVerifyPayment = useCallback(verifyCashfreePayment, [verifyCashfreePayment]);
  const stableSetParams = useCallback(setSearchParams, [setSearchParams]);

  useEffect(() => {
    const id = searchParams.get("merchant_order_id");
    if (!id) return;
    const verify = async () => {
      const res = await stableVerifyPayment(id);
      if (res.success) {
        toast.success("Payment successful!");
        sessionStorage.removeItem("activeSubscriptionOffer");
        setRestoredOffer(null);
        await useBuilderStatusStore.getState().fetchBuilderStatus();
        await fetchBuilderServiceAreas();
        await fetchPendingExpansionRequests();
        await fetchProfile();
        searchParams.delete("merchant_order_id");
        stableSetParams(searchParams);
        setTimeout(() => navigate("/builder/dashboard"), 2000);
      } else {
        toast.error("Payment not verified.");
        searchParams.delete("merchant_order_id");
        stableSetParams(searchParams);
      }
    };
    verify();
  }, [searchParams, stableVerifyPayment, stableSetParams, navigate, fetchBuilderServiceAreas, fetchPendingExpansionRequests, fetchProfile]);

  useEffect(() => {
    if (!user?.id) {
      navigate("/builder/dashboard");
      return;
    }
    fetchBuilderServiceAreas();
    if (!profile) fetchProfile();
  }, [user, fetchBuilderServiceAreas, navigate, profile, fetchProfile]);

  const { status: builderStatus } = useBuilderStatusStore();
  const subscriptionType = useMemo(() => {
    if (builderStatus === 'Awaiting_Payment') return "INITIAL";
    if (!customOffer && (builderStatus === 'renewal_due' || builderStatus === 'Subscription_over')) return "RENEWAL";
    return customOffer?.type || "INITIAL";
  }, [customOffer, builderStatus]);

  const isRenewal = subscriptionType === "RENEWAL";
  const isExpansion = subscriptionType === "EXPANSION";

  const formatCurrency = (val) => {
    if (val === null || val === undefined || isNaN(val)) return "₹0.00";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(val);
  };

  const { globalGstSettings } = useServiceAreaStore();
  const cgstRate = globalGstSettings?.cgst_percentage ?? 0;
  const sgstRate = globalGstSettings?.sgst_percentage ?? 0;
  const gstEnabled = globalGstSettings?.gst_enabled ?? false;

  const baseAmount = Number(hasCustomOffer ? (customOffer?.amount || 0) : (totalServiceAreaCost || 0));
  const cgstAmount = gstEnabled ? Number((baseAmount * (cgstRate / 100)).toFixed(2)) : 0;
  const sgstAmount = gstEnabled ? Number((baseAmount * (sgstRate / 100)).toFixed(2)) : 0;
  const totalGst = Number((cgstAmount + sgstAmount).toFixed(2));
  const total = Number((baseAmount + totalGst).toFixed(2));

  const areaCount = builderServiceAreas?.length || 0;
  const planDuration = customOffer?.duration_months || 12;

  const handlePayNow = async () => {
    if (!cashfree) return toast.error("Payment system not ready.");
    const amount = total;
    if (amount <= 0) return toast.error("Invalid amount");
    if (customOffer) sessionStorage.setItem("activeSubscriptionOffer", JSON.stringify(customOffer));
    
    const order = await createCashfreeOrder(amount, planDuration);
    if (!order || !order.paymentSessionId) return;

    cashfree.checkout({
      paymentSessionId: order.paymentSessionId,
      redirectTarget: "_modal"
    }).then(async (result) => {
      if (result?.error) return toast.error(result.error.message || "Payment failed");
      const verify = await verifyCashfreePayment(order.merchantOrderId);
      if (verify.success) {
        sessionStorage.removeItem("activeSubscriptionOffer");
        setRestoredOffer(null);
        navigate("/builder/dashboard");
      } else toast.error("Verification failed");
    });
  };

  const isLoading = isCreatingOrder || isVerifyingPayment;

  return (
    <>
      <ServiceAreaExpansionModal isOpen={isExpansionModalOpen} onClose={() => setIsExpansionModalOpen(false)} />

      <div className="min-h-screen bg-gray-100 pb-20">
        <div className="space-y-4 w-full max-w-full px-1 xs:px-2 lg:px-1">
          {/* Header Card matching Project Management */}
          <div className="bg-white p-4 rounded-lg shadow-sm border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isRenewal ? 'bg-purple-100' : isExpansion ? 'bg-orange-100' : 'bg-blue-100'}`}>
                {isRenewal ? <RefreshCw size={20} className="text-purple-600" /> : isExpansion ? <Maximize size={20} className="text-orange-600" /> : <ShieldCheck size={20} className="text-blue-600" />}
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">Subscription & Services</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Manage your service areas and plan validity</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!isRenewal && !isExpansion && view === "payment" && (
                <button
                  onClick={() => setIsExpansionModalOpen(true)}
                  disabled={hasCustomOffer}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500 bg-blue-50 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition disabled:opacity-50"
                >
                  <PlusCircle size={16} />
                  <span className="hidden sm:inline">Expand Services</span>
                </button>
              )}

              {view === "payment" ? (
                <button
                  onClick={() => setView("history")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition"
                >
                  <History size={16} />
                  <span>View History</span>
                </button>
              ) : (
                <button
                  onClick={() => setView("payment")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 text-xs font-bold text-white hover:bg-blue-700 transition"
                >
                  <ChevronRight size={16} className="rotate-180" />
                  <span>Plan Details</span>
                </button>
              )}
            </div>
          </div>

          {view === "history" ? (
            <div className="w-full">
              <SubscriptionHistoryTile />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Main Banner Card */}
              <div
                className={`relative overflow-hidden rounded-lg bg-gradient-to-r ${isRenewal
                  ? "from-purple-600 to-indigo-700"
                  : isExpansion
                    ? "from-orange-500 to-red-600"
                    : "from-blue-600 to-blue-700"
                  } text-white p-6 shadow-sm border border-black/5`}
              >
                <div aria-hidden className="pointer-events-none absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
                    backgroundSize: "20px 20px"
                  }}
                />
                <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                       <span className="px-2 py-1 bg-white/20 rounded-md text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm">
                        {subscriptionType} PLAN
                      </span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                      {isRenewal ? "Renew Subscription" : isExpansion ? "Activate Expansion" : "Review & Complete Payment"}
                    </h2>
                    <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-white/90">
                      <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full border border-white/10">
                        <MapPin size={14} />
                        <span>{areaCount} Active Areas</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full border border-white/10">
                        <Tag size={14} />
                        <span>{planDuration} Months Validity</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 flex flex-col items-center justify-center min-w-[160px]">
                    <span className="text-xs uppercase font-bold text-white/70 mb-1">Total Due</span>
                    <span className="text-3xl font-black">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>

              {/* Status Alerts */}
              {(isRenewal || isExpansion) && hasCustomOffer && (
                 <div className={`p-4 rounded-lg flex items-start gap-4 border ${isRenewal ? 'bg-purple-50 border-purple-100 text-purple-800' : 'bg-green-50 border-green-100 text-green-800'}`}>
                    <div className={`p-2 rounded-md ${isRenewal ? 'bg-purple-200' : 'bg-green-200'}`}>
                      {isRenewal ? <RefreshCw size={18} /> : <BadgeCheck size={18} />}
                    </div>
                    <div>
                      <h3 className="font-bold text-sm">{isRenewal ? 'Renewal Notice' : 'Expansion Approved'}</h3>
                      <p className="text-xs mt-1 opacity-90">
                        {isRenewal 
                          ? `This payment will extend your current subscription for another ${planDuration} months for all ${areaCount} active areas.`
                          : 'Your request to expand service areas has been approved. Complete this payment to activate your new locations.'}
                      </p>
                    </div>
                 </div>
              )}

              {/* Details & Payment Grid */}
              <div className="grid lg:grid-cols-3 gap-4">
                {/* Area List Card */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border overflow-hidden">
                  <div className="px-4 py-3 border-b bg-gray-50/50 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-gray-700">Service Area Breakdown</h3>
                    <span className="px-2 py-0.5 bg-gray-200 rounded text-[10px] font-bold text-gray-600">{areaCount} Areas</span>
                  </div>
                  
                  <div className="p-4">
                    {builderServiceAreas && builderServiceAreas.length > 0 ? (
                      <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                        {builderServiceAreas.map((area) => (
                          <div key={area.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-blue-200 transition-colors bg-white group shadow-sm">
                            <div className="flex gap-4">
                              <div className="space-y-0.5">
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">District</p>
                                <p className="text-sm font-bold text-gray-700">{area.district}</p>
                              </div>
                              <div className="w-px h-8 bg-gray-100 mt-1" />
                              <div className="space-y-0.5">
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">Taluk</p>
                                <p className="text-sm font-bold text-gray-700">{area.taluk}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-black text-gray-900">{formatCurrency(area.price)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                        <MapPin size={32} className="opacity-20 mb-2" />
                        <p className="text-sm font-medium">No active areas found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary & Checkout Card */}
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <div className="px-4 py-3 border-b bg-gray-50/50">
                      <h3 className="text-sm font-bold text-gray-700">Payment Summary</h3>
                    </div>
                    
                    <div className="p-4 space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Subtotal</span>
                          <span className="font-bold text-gray-700">{formatCurrency(baseAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">GST ({cgstRate + sgstRate}%)</span>
                          <span className="font-bold text-gray-700">{formatCurrency(totalGst)}</span>
                        </div>
                        <div className="pt-2 border-t flex justify-between items-baseline">
                          <span className="text-sm font-bold text-gray-900">Total Amount</span>
                          <span className="text-xl font-black text-blue-600">{formatCurrency(total)}</span>
                        </div>
                      </div>

                      <button
                        onClick={handlePayNow}
                        disabled={isLoading || isAreasLoading || total <= 0}
                        className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-200 text-white rounded-lg font-bold shadow-md shadow-green-100 transition-all flex items-center justify-center gap-2"
                      >
                        {isLoading ? <Loader2 size={18} className="animate-spin" /> : <>Pay Secured with Cashfree <ChevronRight size={16} /></>}
                      </button>

                      <div className="pt-4 border-t space-y-2">
                        <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase">
                          <Lock size={12} className="text-blue-500" /> Secure Checkout
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                           <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 rounded-md border border-gray-100">
                             <BadgeCheck size={14} className="text-blue-600" />
                             <span className="text-[10px] font-bold text-gray-600">PCI DSS</span>
                           </div>
                           <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 rounded-md border border-gray-100">
                             <ShieldCheck size={14} className="text-blue-600" />
                             <span className="text-[10px] font-bold text-gray-600">256-BIT SSL</span>
                           </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SubscriptionTile;
